<template>
    <div v-if="items.length" class="clinic-features-row" :class="{'clinic-features-row--labeled': labeled}">
        <BaseTooltip
            v-for="item in items"
            :key="item.code"
            class="clinic-features-row__tooltip"
            :z-index="20"
        >
            <template #toltipTrigger>
                <span class="clinic-features-row__item">
                    <component
                        :is="item.icon"
                        filled
                        :font-controlled="false"
                        aria-hidden="true"
                    />
                    <span v-if="labeled" class="clinic-features-row__name">{{ item.name }}</span>
                    <span v-else class="visually-hidden">{{ item.title }}</span>
                </span>
            </template>
            <template #toltipContent>
                {{ item.title }}
            </template>
        </BaseTooltip>
    </div>
</template>

<script lang="ts" setup>
import {CLINIC_FEATURES_IN_CARD, clinicFeature} from '~/utils/clinicFeatures';

const props = withDefaults(defineProps<{
    /** Коды оснащения из API, уже отсортированные бэкендом по приоритету показа. */
    codes?: string[];
    limit?: number;
    labeled?: boolean;
}>(), {
    codes: () => [],
    limit: CLINIC_FEATURES_IN_CARD,
    labeled: false,
});

const items = computed(() =>
    props.codes
        .map((code) => ({code, feature: clinicFeature(code)}))
        .filter((entry): entry is {code: string; feature: NonNullable<ReturnType<typeof clinicFeature>>} => entry.feature !== null)
        .slice(0, props.labeled ? props.codes.length : props.limit)
        .map(({code, feature}) => ({
            code,
            icon: feature.icon,
            title: `${feature.name} — ${feature.hint}`,
            name: feature.name,
        })),
);
</script>

<style lang="scss">
.clinic-features-row {
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    margin: 0;
    padding: 0;

    &__tooltip {
        flex-shrink: 0;

        .base-tooltip__content-wrap {
            width: max-content;
            max-width: min(280px, calc(100vw - 32px));
            padding: 8px 12px;
        }
    }

    &__item {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background-color: $color-dark-white;
        color: $color-accent-text;
        flex-shrink: 0;

        svg {
            display: block;
            width: 18px;
            height: 18px;
            /* nuxt-svgo без `filled` вешает fill:currentColor на все path —
               штриховые иконки из макета превращаются в кляксы. */
            overflow: visible;
        }

        &:hover {
            background-color: $color-pink;
        }
    }

    &--labeled {
        flex-wrap: wrap;
        gap: 8px 12px;

        .clinic-features-row__item {
            width: auto;
            height: auto;
            padding: 8px 12px 8px 8px;
            gap: 8px;
            border-radius: 12px;
            background: $color-dark-white;

            svg {
                width: 28px;
                height: 28px;
                padding: 5px;
                border-radius: 8px;
                background: $color-white;
            }
        }

        .clinic-features-row__name {
            font-size: 14px;
            line-height: 1.3;
            color: $color-black;
            white-space: nowrap;
        }
    }
}
</style>
